import { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  useCreateDiagnosticReceipt,
  useAvailableDiagnosticTests,
  useActiveDiagnosticCategories,
  usePatient,
  usePatientDueReceipts,
  useSettings,
} from '../../services/httpServices/queries';
import { useActiveVisitsByPatient } from '../../services/httpServices/queries/useVisits';
import { useDebounce } from '../../hooks/useDebounce';
import { useListKeyboardNav } from '../../hooks/useListKeyboardNav';
import { PatientVisitSelector } from '../../components/ui/patient-visit-selector';
import { QuickPatientModal } from '../../components/ui/quick-patient-modal';
import { DueReceiptPromptModal } from '../../components/ui/DueReceiptPromptModal';
import { ReferralSearchSelect } from '../../components/ui/ReferralSearchSelect';
import { QuickReferralModal } from '../../components/ui/QuickReferralModal';
import { DoctorSearchSelect } from '../../components/ui/DoctorSearchSelect';
import { QuickDoctorModal } from '../../components/ui/QuickDoctorModal';
import { InfoHint } from '../../components/ui/InfoHint';
import { Pagination } from '../../components/ui/Pagination';
import { DateTimePicker } from '../../components/ui/DatePicker';
import { usePermissions } from '../../hooks/usePermissions';
import {
  DEFAULT_HOSPITAL_TIMEZONE,
  datetimeLocalToISOInTimeZone,
  toDatetimeLocalValueInTimeZone,
} from '../../lib/timezones';
import { ValidatedNumberInput } from '../../components/ui/validated-number-input';
import { ReceivedExcessModal } from '../../components/ui/ReceivedExcessModal';
import { ReferralZeroPayoutModal } from '../../components/ui/ReferralZeroPayoutModal';
import { ReceiptDiscountLimitModal } from '../../components/ui/ReceiptDiscountLimitModal';
import {
  Loader2,
  AlertCircle,
  Check,
  X,
  Search,
  ArrowLeft,
  Receipt,
  ClipboardList,
  CheckCircle2,
  FlaskConical,
  Printer,
  Globe,
} from 'lucide-react';
import { diagnosticService } from '../../services/httpServices/diagnosticService';
import { printDiagnosticReceiptPdf } from '../../utils/printDocument';
import { joinFullName } from '../../utils/patientName';
import { normalizeMobile } from '../../utils/patientMobile';
import type { ReferralPartner } from '../../services/httpServices/referralService';

/**
 * The collection group a test belongs to on a diagnostic receipt. Mirrors the
 * backend `planSpecimens()` grouping key: one tube per (department, container).
 * Procedure-only tests (no specimen) are their own group — one consumable set
 * per procedure. Consumables linked to a test are billed once per group, so a
 * tube shared by several tests is charged once while separate draws are not
 * missed.
 */
function containerLinkFor(test: any) {
  return (test?.accessories || []).find((link: any) => link.isSpecimenContainer);
}

/** Display name for the category a test is grouped under. */
function categoryNameFor(test: any): string {
  return test?.diagnosticCategory?.name || test?.category || 'Other';
}

function collectionGroupKey(test: any): string {
  const departmentId = test?.diagnosticDepartmentId || test?.department?.id || 'other';
  const containerLink = containerLinkFor(test);
  const containerId = containerLink?.diagnosticAccessoryId || containerLink?.accessory?.id;
  if (containerId) {
    return `${departmentId}::${containerId}`;
  }
  return `TEST::${test?.id}`;
}

/**
 * Resolved amount of the counter extra discount. A percentage is taken from the
 * gross total and capped at 100%; a flat amount never exceeds the bill. Kept in
 * one place so the live preview and the "Received follows the discount" handler
 * can never disagree.
 */
function computeExtraDiscountAmount(
  type: 'FLAT' | 'PERCENT',
  value: number,
  totalAmount: number,
): number {
  if (!Number.isFinite(value) || value <= 0) return 0;
  if (type === 'PERCENT') {
    return Number(((totalAmount * Math.min(value, 100)) / 100).toFixed(2));
  }
  return Number(Math.min(value, totalAmount).toFixed(2));
}

export function DiagnosticReceiptForm() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // The selected category is kept in the URL (`?category=<id>`) so the filter
  // survives a page reload or a shared link.
  const activeCategory = searchParams.get('category') || 'ALL';
  const setActiveCategory = (categoryId: string) => {
    const next = new URLSearchParams(searchParams);
    if (!categoryId || categoryId === 'ALL') {
      next.delete('category');
    } else {
      next.set('category', categoryId);
    }
    setSearchParams(next, { replace: true });
  };
  const [patientId, setPatientId] = useState('');
  const [visitId, setVisitId] = useState('');
  const [selectedTests, setSelectedTests] = useState<Map<string, { qty: number; discountPercent: number; test: any }>>(new Map());
  // Auto-added consumables are derived from the selected tests (see
  // `selectedAccessoryItems`), so their quantity always tracks specimen grouping.
  // These two only remember explicit counter-staff intent: accessories they
  // removed, and manual quantity edits.
  const [removedAccessoryIds, setRemovedAccessoryIds] = useState<Set<string>>(new Set());
  const [accessoryQtyOverrides, setAccessoryQtyOverrides] = useState<Map<string, number>>(new Map());
  const [searchQuery, setSearchQuery] = useState('');
  const [testsPage, setTestsPage] = useState(1);
  const [paymentMode, setPaymentMode] = useState('cash');
  const [transactionReference, setTransactionReference] = useState('');
  // Referral — a single payee that may be a Doctor, an internal Employee or an
  // outside Other referrer.
  const [referralId, setReferralId] = useState('');
  const [referralName, setReferralName] = useState('');
  const [referralType, setReferralType] = useState<'DOCTOR' | 'EMPLOYEE' | 'OTHER' | ''>('');
  // Which referral kind the selector is filtered to (Doctor / Employee / Other).
  const [referralTypeFilter, setReferralTypeFilter] = useState<'ALL' | 'DOCTOR' | 'EMPLOYEE' | 'OTHER'>('ALL');
  // Effective referral share % for THIS receipt. Prefilled from the partner's
  // default and freely overridable at the counter (scoped to this receipt only).
  const [referralSharePercent, setReferralSharePercent] = useState<number>(0);
  // Until the counter edits the share, it follows the allocated base
  // (base − patient item discount) automatically.
  const [referralShareTouched, setReferralShareTouched] = useState(false);
  // Reference-only doctor — informational, never part of revenue share.
  const [doctorReferenceId, setDoctorReferenceId] = useState('');
  const [doctorReferenceName, setDoctorReferenceName] = useState('');
  const [doctorRecommendation, setDoctorRecommendation] = useState('');
  const [paidAmount, setPaidAmount] = useState<number>(0);
  const [extraDiscountType, setExtraDiscountType] = useState<'FLAT' | 'PERCENT'>('FLAT');
  const [extraDiscountValue, setExtraDiscountValue] = useState<number>(0);
  const [formError, setFormError] = useState<string | null>(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showPayableWarning, setShowPayableWarning] = useState(false);
  const [showReferralPayoutWarning, setShowReferralPayoutWarning] = useState(false);
  const [showDiscountLimitWarning, setShowDiscountLimitWarning] = useState(false);
  const [createdReceipt, setCreatedReceipt] = useState<any>(null);

  const [showQuickPatientModal, setShowQuickPatientModal] = useState(false);
  // Name/mobile typed into the patient search, prefilled into the quick form.
  const [patientQuery, setPatientQuery] = useState('');

  const [showQuickReferralModal, setShowQuickReferralModal] = useState(false);
  const [quickReferralQuery, setQuickReferralQuery] = useState('');

  // Quick add-doctor dialog for the Doctor Reference field.
  const [showQuickDoctorModal, setShowQuickDoctorModal] = useState(false);
  const [quickDoctorQuery, setQuickDoctorQuery] = useState('');

  const TESTS_PER_PAGE = 20;
  const debouncedSearch = useDebounce(searchQuery, 400);

  const { data: testsData, isLoading: testsLoading, isFetching: testsFetching } =
    useAvailableDiagnosticTests(
      testsPage,
      TESTS_PER_PAGE,
      activeCategory,
      debouncedSearch.trim() || undefined,
    );
  const { isLoading: visitsLoading } = useActiveVisitsByPatient(patientId);
  const createMutation = useCreateDiagnosticReceipt();
  const { data: categoriesData } = useActiveDiagnosticCategories();

  // Receipt date & time. The receipt's `createdAt` is its business date, so a
  // backdated entry must be an explicit, per-role capability. Roles without
  // `diagnostics:receipt:backdate` never send a date and the server stamps the
  // submit instant instead.
  const { hasPermission } = usePermissions();
  const canBackdate = hasPermission('diagnostics:receipt:backdate');
  const [receiptDate, setReceiptDate] = useState(() =>
    toDatetimeLocalValueInTimeZone(new Date()),
  );
  const [receiptDateError, setReceiptDateError] = useState<string | undefined>();

  // Hospital timezone configured on Settings → General (`hospital_timezone`).
  // Stays `undefined` until settings settle so the seed below uses the
  // configured zone (or the shared Asia/Dhaka fallback), not the browser's.
  const { data: settingsData } = useSettings();
  const hospitalTimezone = useMemo(() => {
    if (!settingsData) return undefined;
    const value = settingsData.data?.find((s: any) => s.key === 'hospital_timezone')?.value;
    return (typeof value === 'string' ? value.trim() : '') || DEFAULT_HOSPITAL_TIMEZONE;
  }, [settingsData]);

  // Hospital's allocated base discount for referred receipts. The patient's
  // item discount is subtracted from it and the remainder becomes the referral
  // commission (e.g. 40% base − 20% patient = 20% referral).
  const referralBasePercent = useMemo(() => {
    const rawValue = settingsData?.data?.find(
      (s: any) => s.key === 'referral_base_discount_percent',
    )?.value;
    const raw =
      rawValue === undefined || rawValue === null || String(rawValue).trim() === ''
        ? Number.NaN
        : Number(rawValue);
    return Number.isFinite(raw) ? Math.max(0, Math.min(100, raw)) : 40;
  }, [settingsData]);

  // Roles holding the override permission may set the Receipt Base Discount for
  // THIS receipt. The field starts at 100% and replaces the setting above;
  // without the permission the configured value is always used.
  const canOverrideBaseDiscount = hasPermission(
    'diagnostics:receipt:override_base_discount',
  );
  const [baseDiscountOverride, setBaseDiscountOverride] = useState<number>(100);
  const effectiveBasePercent = canOverrideBaseDiscount
    ? Math.max(0, Math.min(100, Number(baseDiscountOverride) || 0))
    : referralBasePercent;

  // Guards the one-time seed so a later settings refetch can't overwrite a
  // backdate the counter staff picked deliberately.
  const seededReceiptTimeRef = useRef(false);
  useEffect(() => {
    if (!hospitalTimezone || seededReceiptTimeRef.current) return;
    seededReceiptTimeRef.current = true;
    setReceiptDate(toDatetimeLocalValueInTimeZone(new Date(), hospitalTimezone));
  }, [hospitalTimezone]);

  // The patient's other receipts with an outstanding balance are matched by
  // mobile number (never by name), so the patient record is loaded alongside
  // the visit list.
  const { data: selectedPatientData } = usePatient(patientId);
  const selectedPatient = selectedPatientData?.data;
  const { data: patientDueReceipts = [] } = usePatientDueReceipts(
    { id: patientId, mobileNumber: selectedPatient?.mobileNumber },
    { enabled: Boolean(patientId) && Boolean(selectedPatient) },
  );
  const duePromptKey = patientId
    ? `${patientId}|${normalizeMobile(selectedPatient?.mobileNumber)}`
    : '';
  // Dismissal is remembered per selected patient so skipping the prompt does
  // not make it pop up again for the same patient on every keystroke.
  const [dismissedDuePromptKey, setDismissedDuePromptKey] = useState<string | null>(null);
  const showDueReceiptPrompt =
    Boolean(patientId) &&
    duePromptKey !== dismissedDuePromptKey &&
    patientDueReceipts.length > 0;

  // A different patient is a fresh decision: clear the previous skip.
  useEffect(() => {
    setDismissedDuePromptKey(null);
  }, [patientId]);

  // Tests for the current page, filtered/sorted server-side.
  const tests = testsData?.data || [];
  const testsMeta = testsData?.meta;

  // Categories always come from the diagnostic-categories administration data,
  // not derived from the page of tests, so every category is available.
  const categories = useMemo(
    () =>
      (categoriesData?.data || [])
        .slice()
        .sort((a: any, b: any) =>
          (a.displayOrder ?? 0) - (b.displayOrder ?? 0) || String(a.name).localeCompare(String(b.name)),
        ),
    [categoriesData],
  );

  // Jump back to the first page whenever the filter or search changes.
  useEffect(() => {
    setTestsPage(1);
  }, [activeCategory, debouncedSearch]);

  /* ── selected line items ── */
  const selectedItems = useMemo(() => {
    // Selected tests carry their own record so the receipt summary stays intact
    // even when the test is no longer part of the current server-side page.
    return Array.from(selectedTests.entries()).map(([testId, data]) => ({
      testId,
      qty: data.qty,
      discountPercent: data.discountPercent,
      test: data.test,
    }));
  }, [selectedTests]);

  const selectedAccessoryItems = useMemo(() => {
    // Collect consumables from the selected tests, grouped by how the lab will
    // draw them. Each accessory is charged `defaultQuantity × number of groups`,
    // unless the counter staff set an explicit override.
    const groups = new Map<string, {
      accessoryId: string;
      name: string;
      price: number;
      defaultQty: number;
      groupKeys: Set<string>;
    }>();

    for (const { test } of selectedItems) {
      for (const link of test.accessories || []) {
        const accessoryId = link.diagnosticAccessoryId || link.accessory?.id;
        if (!accessoryId || removedAccessoryIds.has(accessoryId)) continue;
        const record = groups.get(accessoryId) || {
          accessoryId,
          name: link.accessory?.name || 'Accessory',
          price: Number(link.accessory?.price) || 0,
          defaultQty: 1,
          groupKeys: new Set<string>(),
        };
        record.defaultQty = Math.max(record.defaultQty, Number(link.defaultQuantity) || 1);
        record.groupKeys.add(collectionGroupKey(test));
        groups.set(accessoryId, record);
      }
    }

    return Array.from(groups.values()).map((record) => {
      const override = accessoryQtyOverrides.get(record.accessoryId);
      return {
        accessoryId: record.accessoryId,
        name: record.name,
        price: record.price,
        qty: override ?? record.defaultQty * record.groupKeys.size,
        isManualOverride: override !== undefined,
      };
    });
  }, [selectedItems, removedAccessoryIds, accessoryQtyOverrides]);

  const testsTotalAmount = selectedItems.reduce(
    (sum, item) => sum + (item.test.price || 0) * item.qty,
    0,
  );
  const totalAmount = testsTotalAmount
    + selectedAccessoryItems.reduce((sum, item) => sum + (item.price || 0) * item.qty, 0);

  // Per-item percentage discounts.
  const totalDiscountAmount = selectedItems.reduce((sum, item) => {
    return sum + ((item.test.price || 0) * item.qty * item.discountPercent / 100);
  }, 0);

  // Accessories carry no discount, so the shown rate must be based on the
  // discount-eligible tests only (20% stays 20%, not 170/880 = 19.32%).
  const itemDiscountPercent =
    testsTotalAmount > 0
      ? Number(((totalDiscountAmount / testsTotalAmount) * 100).toFixed(2))
      : 0;

  // Referral's default share = allocated base discount − patient item discount.
  // e.g. base 40% − 20% item discount = 20% of the test gross.
  const autoReferralPercent = Number(
    Math.max(0, Math.min(100, effectiveBasePercent - itemDiscountPercent)).toFixed(2),
  );
  useEffect(() => {
    if (referralId && !referralShareTouched) {
      setReferralSharePercent(autoReferralPercent);
    }
  }, [autoReferralPercent, referralId, referralShareTouched]);

  // Extra discount entered at the counter — either a flat amount or a
  // percentage of the gross total. It is combined with the item discounts
  // before the payable/due amounts are derived.
  const extraDiscountAmount = useMemo(
    () => computeExtraDiscountAmount(extraDiscountType, extraDiscountValue, totalAmount),
    [extraDiscountType, extraDiscountValue, totalAmount],
  );

  const combinedDiscountAmount = Number((totalDiscountAmount + extraDiscountAmount).toFixed(2));
  const combinedDiscountPercent =
    testsTotalAmount > 0
      ? Number(((combinedDiscountAmount / testsTotalAmount) * 100).toFixed(2))
      : 0;

  // The receipt base discount (Settings → General) is the hospital's maximum
  // discount budget, so the receipt's total discount can never exceed it.
  const discountExceedsBase =
    combinedDiscountPercent > effectiveBasePercent + 1e-9;

  const effectiveReferralPercent = Math.max(0, Math.min(100, Number(referralSharePercent) || 0));

  /**
   * Referral commission = the referral's share % of the discount-eligible
   * (test) gross. The patient keeps the whole entered discount — the commission
   * is a HOSPITAL expense paid from the allocated base, so it is never
   * subtracted from the patient's discount or added to the bill.
   */
  // Whole taka, mirroring the backend.
  const referralCommissionAmount =
    referralId && effectiveReferralPercent > 0
      ? Math.min(
          Math.round((testsTotalAmount * effectiveReferralPercent) / 100),
          Math.floor(testsTotalAmount + 1e-9),
        )
      : 0;
  const patientDiscountAmount = combinedDiscountAmount;

  // Amount payable after the patient's full discount. The referral commission is
  // a hospital expense and does not affect the patient's payable.
  const netPayableAmount = Math.max(0, Number((totalAmount - patientDiscountAmount).toFixed(2)));
  const dueAmount = Math.max(0, Number((netPayableAmount - paidAmount).toFixed(2)));
  const excessAmount = Math.max(0, Number((paidAmount - netPayableAmount).toFixed(2)));
  const isExcessReceived = excessAmount > 0;
  const isFullyPaid = !isExcessReceived && dueAmount === 0 && netPayableAmount > 0;

  /**
   * Applies an extra-discount edit (amount or ৳/% toggle). When the counter has
   * already typed an amount into Received, Received follows the new payable so
   * the receipt stays settled as the discount moves. A blank/0 Received is an
   * intentionally unpaid receipt and is left alone.
   */
  const applyExtraDiscount = (type: 'FLAT' | 'PERCENT', value: number) => {
    setExtraDiscountType(type);
    setExtraDiscountValue(value);
    if (paidAmount <= 0) return;
    const nextExtraDiscount = computeExtraDiscountAmount(type, value, totalAmount);
    const nextCombinedDiscount = Number((totalDiscountAmount + nextExtraDiscount).toFixed(2));
    setPaidAmount(Math.max(0, Number((totalAmount - nextCombinedDiscount).toFixed(2))));
  };

  /**
   * Projected referral payout for this receipt. Mirrors the backend exactly:
   * the commission is the referral % of the TEST gross (the extra counter
   * discount is not a commission base). Accessories never carry a payout line.
   */
  const referralProjection = useMemo(() => {
    if (!referralId || selectedItems.length === 0) return null;
    const gross = totalAmount;
    const discount = combinedDiscountAmount;
    const net = gross - discount;
    // Commission is the referral % of the TEST gross (mirrors the backend).
    let doctorShare = Math.min(
      Math.round((testsTotalAmount * effectiveReferralPercent) / 100),
      Math.floor(testsTotalAmount + 1e-9),
    );
    if (doctorShare < 0) doctorShare = 0;
    if (doctorShare > net) doctorShare = Math.floor(net);
    return {
      percent: effectiveReferralPercent,
      gross,
      discount,
      doctorShare,
      // The hospital pays the commission from its margin, so the referral
      // reduces the hospital's net but never the patient's payable.
      hospitalShare: Number(Math.max(0, net - doctorShare).toFixed(2)),
    };
  }, [referralId, effectiveReferralPercent, selectedItems.length, totalAmount, testsTotalAmount, combinedDiscountAmount]);

  // Only surface the positive-share preview; the zero-payout case is handled
  // by its own inline warning and confirmation modal below.
  const referralPreview =
    referralProjection && referralProjection.percent > 0 ? referralProjection : null;

  // A referral is selected but the receipt would accrue no commission. This is
  // true only when the effective share is 0%. Such a receipt silently produces
  // no payout.
  const referralPayoutIsZero =
    !!referralId &&
    !!referralProjection &&
    Math.round(referralProjection.doctorShare * 100) / 100 <= 0;

  const referralZeroReason = useMemo(() => {
    if (!referralPayoutIsZero) return '';
    if (effectiveReferralPercent <= 0) {
      return 'Referral % is 0%. Set it above 0% to create a referral payout.';
    }
    return 'Referral % is 0%, so no commission will be booked. Raise the share above 0% to create a referral payout. The counter extra discount is never part of the commission base.';
  }, [referralPayoutIsZero, effectiveReferralPercent]);

  /* ── actions ── */
  const addTest = (test: any) => {
    setSelectedTests((prev) =>
      new Map(prev).set(test.id, {
        qty: 1,
        discountPercent: test?.discountPercent || 0,
        test,
      }),
    );
    // Re-adding a test clears any earlier "removed" marks so its consumables
    // reappear automatically (accessories are derived, never stored).
    const accessories = test?.accessories;
    if (accessories?.length) {
      setRemovedAccessoryIds((prev) => {
        const next = new Set(prev);
        let changed = false;
        for (const ta of accessories) {
          const accId = ta.diagnosticAccessoryId || ta.accessory?.id;
          if (accId && next.delete(accId)) changed = true;
        }
        return changed ? next : prev;
      });
    }
  };

  const removeTest = (testId: string) => {
    setSelectedTests((prev) => {
      const next = new Map(prev);
      next.delete(testId);
      return next;
    });
    // Accessories are derived from the remaining tests, so shared consumables
    // (e.g. a tube used by another still-selected test) correctly stay put.
  };

  // Keyboard navigation for the available-tests list: Up/Down to move the
  // highlight, Enter to add the highlighted test to the receipt ("add to cart").
  const testNav = useListKeyboardNav({
    itemCount: tests.length,
    onSelect: (index) => {
      const test = tests[index];
      if (!test || selectedTests.has(test.id)) return;
      addTest(test);
    },
    isDisabled: (index) => {
      const test = tests[index];
      return !test || selectedTests.has(test.id);
    },
    resetKey: `${debouncedSearch}|${activeCategory}|${testsPage}`,
  });

  const updateQty = (testId: string, qty: number) => {
    if (qty < 1) {
      removeTest(testId);
      return;
    }
    setSelectedTests((prev) => {
      const next = new Map(prev);
      const existing = next.get(testId);
      if (existing) {
        next.set(testId, { ...existing, qty });
      }
      return next;
    });
  };

  const updateTestDiscount = (testId: string, discountPercent: number) => {
    setSelectedTests((prev) => {
      const next = new Map(prev);
      const existing = next.get(testId);
      if (existing) {
        next.set(testId, { ...existing, discountPercent: Math.max(0, Math.min(100, discountPercent)) });
      }
      return next;
    });
  };

  const removeAccessory = (accessoryId: string) => {
    setRemovedAccessoryIds((prev) => new Set(prev).add(accessoryId));
    setAccessoryQtyOverrides((prev) => {
      const next = new Map(prev);
      next.delete(accessoryId);
      return next;
    });
  };

  const updateAccessoryQty = (accessoryId: string, qty: number) => {
    if (!Number.isFinite(qty) || qty < 1) {
      removeAccessory(accessoryId);
      return;
    }
    // Setting an explicit quantity means the user wants this accessory back.
    setRemovedAccessoryIds((prev) => {
      if (!prev.has(accessoryId)) return prev;
      const next = new Set(prev);
      next.delete(accessoryId);
      return next;
    });
    setAccessoryQtyOverrides((prev) => new Map(prev).set(accessoryId, qty));
  };

  const handleSubmit = () => {
    setFormError(null);
    if (!patientId) {
      setFormError('Please select a patient');
      return;
    }
    if (selectedTests.size === 0) {
      setFormError('Please add at least one test');
      return;
    }
    if (discountExceedsBase) {
      setShowDiscountLimitWarning(true);
      return;
    }
    if (canBackdate && !receiptDate) {
      setReceiptDateError('Receipt date & time is required');
      setFormError('Please choose the receipt date & time');
      return;
    }
    setReceiptDateError(undefined);
    if (isExcessReceived) {
      setShowPayableWarning(true);
      return;
    }
    // Gate creation on an explicit acknowledgement: a referral with a 0% share
    // books no distribution, so it never reaches the payout ledger. Warn before
    // the counter raises a receipt that looks referral-linked but pays nobody.
    if (referralPayoutIsZero) {
      setShowReferralPayoutWarning(true);
      return;
    }
    void performCreate();
  };

  const handleCreateWithoutPayout = () => {
    setShowReferralPayoutWarning(false);
    void performCreate();
  };

  const performCreate = async () => {
    const items = Array.from(selectedTests.entries()).map(([diagnosticTestId, data]) => ({
      diagnosticTestId,
      quantity: Number.isFinite(data.qty) && data.qty > 0 ? data.qty : 1,
      discountPercent: data.discountPercent || 0,
    }));

    const accessoryItems = selectedAccessoryItems.map((item) => ({
      diagnosticAccessoryId: item.accessoryId,
      quantity: Number.isFinite(item.qty) && item.qty > 0 ? item.qty : 1,
    }));

    try {
      const result = await createMutation.mutateAsync({
        patientId,
        visitId: visitId || undefined,
        // Omitted for roles without the backdate permission: the server records
        // the actual submit instant instead.
        receiptDate: canBackdate
          ? datetimeLocalToISOInTimeZone(receiptDate, hospitalTimezone)
          : undefined,
        items,
        accessoryItems: accessoryItems.length > 0 ? accessoryItems : undefined,
        paymentMode,
        discountPercent: itemDiscountPercent,
        extraDiscountType,
        extraDiscountValue,
        // Per-receipt ceiling override for roles allowed to raise it. Omitted
        // otherwise so the server falls back to Settings → General.
        baseDiscountPercent: canOverrideBaseDiscount
          ? effectiveBasePercent
          : undefined,
        paidAmount,
        transactionReference: transactionReference || undefined,
        // Referral + the effective (possibly overridden) share for THIS receipt.
        // The backend snapshots it; the partner's default is untouched.
        referralId: referralId || undefined,
        referralSharePercent: referralId ? effectiveReferralPercent : undefined,
        // Reference-only doctor (informational; no revenue impact).
        doctorReferenceId: doctorReferenceId || undefined,
        doctorReferenceName: doctorReferenceName || undefined,
        doctorRecommendation: doctorRecommendation || undefined,
      });
      setCreatedReceipt(result.data);
      setShowSuccessModal(true);
    } catch (err: any) {
      setFormError(err?.message || 'Failed to create receipt. Please check your selections and try again.');
    }
  };

  const handlePrintAndGo = async () => {
    if (!createdReceipt?.id) return;
    try {
      const url = await diagnosticService.printReceipt(createdReceipt.id);
      await printDiagnosticReceiptPdf(url);
    } catch (err: any) {
      setFormError(err?.message || 'Failed to generate print preview.');
    } finally {
      setShowSuccessModal(false);
      navigate('/opd/diagnostic-receipts', { state: { justCreated: true } });
    }
  };

  const handleGo = () => {
    setShowSuccessModal(false);
    navigate('/opd/diagnostic-receipts', { state: { justCreated: true } });
  };

  /* ── referrals ── */
  const handleSelectReferral = (id: string, partner?: ReferralPartner) => {
    setReferralId(id);
    setReferralName(partner?.name || '');
    setReferralType(partner?.type || '');
    // The share follows the hospital's allocated base by default:
    // base − the patient's item discount. The counter can override it.
    setReferralShareTouched(false);
    setReferralSharePercent(id ? autoReferralPercent : 0);
  };

  const handleClearReferral = () => {
    setReferralId('');
    setReferralName('');
    setReferralType('');
    setReferralShareTouched(false);
    setReferralSharePercent(0);
  };

  const handleClearDoctorReference = () => {
    setDoctorReferenceId('');
    setDoctorReferenceName('');
  };

  const handleReferralCreated = (partner: ReferralPartner) => {
    setShowQuickReferralModal(false);
    handleSelectReferral(partner.id, partner);
  };

  const handleQuickDoctorCreated = (doctor: { id: string; name?: string }) => {
    setShowQuickDoctorModal(false);
    setDoctorReferenceId(doctor.id);
    setDoctorReferenceName(doctor.name || '');
  };

  /* ── render a single test row ── */
  const TestRow = ({ test, index }: { test: any; index: number }) => {
    const isSelected = selectedTests.has(test.id);
    const highlighted = index === testNav.highlightedIndex && !isSelected;
    return (
      <button
        data-nav-index={index}
        onMouseEnter={() => testNav.setHighlightedIndex(index)}
        onClick={() => !isSelected && addTest(test)}
        disabled={isSelected}
        className={`w-full text-left flex items-center justify-between px-4 py-3 transition-colors border-b border-border last:border-0 ${
          isSelected
            ? 'bg-success/5 cursor-default'
            : highlighted
              ? 'bg-background ring-1 ring-inset ring-primary/40 cursor-pointer'
              : 'hover:bg-background cursor-pointer'
        }`}
      >
        <div className="min-w-0 flex items-center gap-3">
          {isSelected ? (
            <CheckCircle2 size={16} className="text-success shrink-0" />
          ) : (
            <div className="w-4 h-4 rounded border border-border shrink-0" />
          )}
          <div>
            <p className={`text-sm font-medium truncate ${isSelected ? 'text-success' : 'text-text'}`}>
              {test.name}
            </p>
            <p className="text-xs text-text-muted truncate">
              {categoryNameFor(test)}
              {(test.room?.name || test.roomNumber) && (
                <> · Room {test.room?.name || test.roomNumber}</>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0 ml-4">
          <span className="text-sm font-bold text-primary whitespace-nowrap">
            ৳{test.price?.toLocaleString()}
          </span>
          {!isSelected && (
            <span className="text-xs bg-primary text-white px-2 py-1 rounded whitespace-nowrap">
              Add
            </span>
          )}
        </div>
      </button>
    );
  };

  return (
    <div className="space-y-6 w-full">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate('/opd/diagnostic-receipts')}
          className="p-2 rounded-lg hover:bg-background text-text-muted transition-colors cursor-pointer"
        >
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-text">New Receipt</h1>
          <p className="text-sm text-text-muted">Select tests and create receipt</p>
        </div>
      </div>

      {formError && (
        <div className="flex items-start gap-2 p-3 bg-danger/10 border border-danger/20 rounded-lg text-sm text-danger">
          <AlertCircle size={16} className="shrink-0 mt-0.5" />
          <span>{formError}</span>
        </div>
      )}

      {/* Main Grid: Browse Tests + Receipt Summary */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">

        {/* Left: Patient & Visit + Browse Tests */}
        <div className="xl:col-span-2 space-y-6">

          {/* Patient & Visit */}
          <div className="bg-surface rounded-xl border border-border p-6">
            <h2 className="text-sm font-semibold text-text uppercase tracking-wide mb-4">Patient & Visit</h2>
            <PatientVisitSelector
              patientId={patientId}
              visitId={visitId}
              // The counter may sell a test to a walk-in with no encounter yet;
              // the API opens/reuses the OPD visit on save. Only OPD visits are
              // offered so a receipt can never be attached to an IPD stay.
              autoCreateVisit
              visitType="OPD"
              onChange={({ patientId: pid, visitId: vid }) => {
                setPatientId(pid);
                setVisitId(vid);
                setFormError(null);
              }}
              onAddNewPatient={(query) => {
                setPatientQuery(query);
                setShowQuickPatientModal(true);
              }}
            />
          </div>

          {/* Search + Category Filters */}
          <div className="bg-surface rounded-xl border border-border p-6 space-y-4">
            <div className="flex items-center gap-2">
              <ClipboardList size={18} className="text-primary" />
              <h2 className="text-sm font-semibold text-text uppercase tracking-wide">Available Tests</h2>
            </div>

            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={testNav.onKeyDown}
                className="w-full pl-10 pr-3 py-2.5 rounded-lg border border-border bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                placeholder="Search by test name..."
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-text"
                >
                  <X size={14} />
                </button>
              )}
            </div>

            {/* Category chips */}
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setActiveCategory('ALL')}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                  activeCategory === 'ALL'
                    ? 'bg-primary text-white'
                    : 'bg-background border border-border text-text-muted hover:text-text'
                }`}
              >
                All Categories
              </button>
              {categories.map((category: any) => (
                <button
                  key={category.id}
                  onClick={() => setActiveCategory(category.id)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                    activeCategory === category.id
                      ? 'bg-primary text-white'
                      : 'bg-background border border-border text-text-muted hover:text-text'
                  }`}
                >
                  {category.name}
                </button>
              ))}
            </div>
          </div>

          {/* Tests List */}
          <div className="bg-surface rounded-xl border border-border overflow-hidden">
            <div className="p-6">
              {testsLoading ? (
                <div className="flex items-center justify-center py-10 text-text-muted">
                  <Loader2 size={20} className="animate-spin mr-2" />
                  <span className="text-sm">Loading tests...</span>
                </div>
              ) : tests.length === 0 ? (
                <div className="text-center py-10">
                  <FlaskConical className="w-8 h-8 text-text-muted mx-auto mb-2" />
                  <p className="text-sm text-text-muted">No tests found</p>
                </div>
              ) : (
                <div
                  ref={testNav.listRef}
                  className={`border border-border rounded-lg overflow-hidden transition-opacity ${
                    testsFetching ? 'opacity-60' : ''
                  }`}
                >
                  {tests.map((test: any, index: number) => (
                    <TestRow key={test.id} test={test} index={index} />
                  ))}
                </div>
              )}
            </div>
            {testsMeta && (
              <Pagination
                page={testsMeta.page}
                totalPages={testsMeta.totalPages}
                total={testsMeta.total}
                limit={testsMeta.limit}
                onPageChange={setTestsPage}
                itemName="tests"
              />
            )}
          </div>
        </div>

        {/* Right: Receipt Summary */}
        <div className="xl:col-span-1">
          <div className="sticky top-6 space-y-4 bg-surface rounded-xl border border-border p-6">
            <div className="flex items-center gap-2">
              <Receipt size={18} className="text-primary" />
              <h2 className="text-sm font-semibold text-text uppercase tracking-wide">Receipt Summary</h2>
            </div>

            {/* Receipt date & time — the receipt's business date (createdAt). */}
            <div>
              <label className="mb-1 flex items-center gap-1 text-xs font-medium text-text-muted uppercase">
                Receipt Date &amp; Time *
                <InfoHint
                  label="Receipt Date & Time"
                  en="The date the receipt is posted on. Changing it (backdating) requires the “Add Backdate Receipt” permission; otherwise the receipt is dated when you save it."
                  bn="রিসিট যে তারিখে পোস্ট হবে। পরিবর্তন (ব্যাকডেট) করতে “Add Backdate Receipt” পারমিশন প্রয়োজন; না থাকলে সেভ করার সময়ের তারিখ ব্যবহৃত হয়।"
                />
              </label>
              <DateTimePicker
                value={receiptDate}
                onChange={(value) => {
                  setReceiptDate(value);
                  if (value) setReceiptDateError(undefined);
                }}
                error={receiptDateError}
                timeZone={hospitalTimezone}
                disabled={!canBackdate}
                clearable={canBackdate}
                required
              />
              <p className="mt-1 flex items-center gap-1 text-[0.625rem] leading-4 text-text-muted">
                <Globe size={10} className="shrink-0" />
                {canBackdate
                  ? `Times shown in ${hospitalTimezone ?? DEFAULT_HOSPITAL_TIMEZONE} (Settings → General)`
                  : 'Recorded automatically when you create the receipt. Requires the “Add Backdate Receipt” permission to change.'}
              </p>
            </div>

            {/* Selected tests */}
            {selectedItems.length === 0 && selectedAccessoryItems.length === 0 ? (
              <div className="border-2 border-dashed border-border rounded-lg p-8 text-center">
                <Receipt className="w-8 h-8 text-text-muted mx-auto mb-2" />
                <p className="text-sm text-text-muted font-medium">No tests added yet</p>
                <p className="text-xs text-text-muted mt-1">Browse tests on the left to add</p>
              </div>
            ) : (
              <div className="overflow-hidden rounded-lg border border-border">
                <table className="w-full table-fixed text-sm">
                  <thead className="bg-background border-b border-border">
                    <tr>
                      <th className="pl-3 pr-1 py-2 text-left text-xs font-medium text-text-muted uppercase">Item</th>
                      <th className="w-[52px] px-1 py-2 text-center text-xs font-medium text-text-muted uppercase">Qty</th>
                      <th className="w-[58px] px-1 py-2 text-center text-xs font-medium text-text-muted uppercase">Disc%</th>
                      <th className="w-[76px] pl-1 pr-2 py-2 text-right text-xs font-medium text-text-muted uppercase">Total</th>
                      <th className="w-6 py-2"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {selectedItems.map(({ testId, qty, discountPercent, test }) => (
                      <tr key={testId} className="hover:bg-background/50">
                        <td className="pl-3 pr-1 py-2.5 min-w-0">
                          <p className="font-medium text-text text-xs truncate" title={test.name}>{test.name}</p>
                          <p className="text-[0.625rem] text-text-muted truncate">{categoryNameFor(test)}</p>
                        </td>
                        <td className="px-1 py-2.5 text-center">
                          <input
                            type="number"
                            min={1}
                            value={qty}
                            onChange={(e) => updateQty(testId, Number(e.target.value))}
                            className="w-full px-0.5 py-1 text-xs rounded border border-border bg-background text-center focus:outline-none focus:ring-1 focus:ring-primary"
                          />
                        </td>
                        <td className="px-1 py-2.5 text-center">
                          <ValidatedNumberInput
                            value={discountPercent}
                            onChange={(v) => updateTestDiscount(testId, v)}
                            max={100}
                            className="w-full px-0.5 py-1 text-xs rounded border border-border bg-background text-center focus:outline-none focus:ring-1 focus:ring-primary"
                          />
                        </td>
                        <td className="pl-1 pr-2 py-2.5 text-right font-medium text-text text-xs whitespace-nowrap">
                          <div>৳{(test.price * qty).toLocaleString()}</div>
                          {discountPercent > 0 && (
                            <div className="text-[0.625rem] text-danger">-৳{Math.round((test.price * qty * discountPercent / 100)).toLocaleString()}</div>
                          )}
                        </td>
                        <td className="py-2.5 text-center">
                          <button
                            onClick={() => removeTest(testId)}
                            className="p-1 rounded hover:bg-danger/10 text-text-muted hover:text-danger transition-colors"
                            title="Remove"
                          >
                            <X size={12} />
                          </button>
                        </td>
                      </tr>
                    ))}
                    {selectedAccessoryItems.map(({ accessoryId, qty, name, price }) => (
                      <tr key={accessoryId} className="hover:bg-background/50 bg-warning/5">
                        <td className="pl-3 pr-1 py-2.5 min-w-0">
                          <p className="font-medium text-text text-xs truncate" title={name}>{name}</p>
                          <p className="text-[0.625rem] text-text-muted truncate">Accessory</p>
                        </td>
                        <td className="px-1 py-2.5 text-center">
                          <input
                            type="number"
                            min={1}
                            value={qty}
                            onChange={(e) => updateAccessoryQty(accessoryId, Number(e.target.value))}
                            className="w-full px-0.5 py-1 text-xs rounded border border-border bg-background text-center focus:outline-none focus:ring-1 focus:ring-primary"
                          />
                        </td>
                        <td className="px-1 py-2.5 text-center">
                          <span className="text-xs text-text-muted">0.00</span>
                        </td>
                        <td className="pl-1 pr-2 py-2.5 text-right font-medium text-text text-xs whitespace-nowrap">
                          ৳{(price * qty).toLocaleString()}
                        </td>
                        <td className="py-2.5 text-center">
                          <button
                            onClick={() => removeAccessory(accessoryId)}
                            className="p-1 rounded hover:bg-danger/10 text-text-muted hover:text-danger transition-colors"
                            title="Remove"
                          >
                            <X size={12} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {(selectedItems.length > 0 || selectedAccessoryItems.length > 0) && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-text-muted">{selectedItems.length} test(s) · {selectedAccessoryItems.length} accessory(s)</span>
                <button
                  onClick={() => {
                    setSelectedTests(new Map());
                    setRemovedAccessoryIds(new Set());
                    setAccessoryQtyOverrides(new Map());
                  }}
                  className="text-xs text-danger hover:underline font-medium"
                >
                  Clear all
                </button>
              </div>
            )}

            <div className="h-px bg-border" />

            {/* Doctor Reference — informational only (no revenue share) */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="flex items-center gap-1 text-xs font-medium text-text-muted uppercase">
                  Doctor Reference
                  <InfoHint
                    label="Doctor Reference"
                    en="A reference-only doctor for this receipt. It is informational and never part of any revenue share or payout."
                    bn="এই রিসিটের জন্য শুধুমাত্র তথ্যের রেফারেন্স ডাক্তার। এটি কোনো রেভিনিউ শেয়ার বা পেমেন্টের অংশ নয়।"
                  />
                </label>
                {doctorReferenceId && (
                  <button
                    type="button"
                    onClick={handleClearDoctorReference}
                    className="text-xs font-medium text-danger hover:underline uppercase"
                  >
                    Clear
                  </button>
                )}
              </div>
              <DoctorSearchSelect
                value={doctorReferenceId}
                onChange={(id, doc) => {
                  setDoctorReferenceId(id);
                  setDoctorReferenceName(doc?.name || '');
                }}
                selectedLabel={doctorReferenceName}
                showPreview={false}
                placeholder="Search doctor by name or code..."
                onAddNew={(query) => {
                  setQuickDoctorQuery(query);
                  setShowQuickDoctorModal(true);
                }}
                addNewLabel="Add new doctor"
              />
            </div>

            {/* Referral Type + Referral + Referral % (one row) */}
            <div className="grid grid-cols-[90px_minmax(0,1fr)_104px] gap-2 items-start pt-1">
              <div>
                <label className="block text-xs font-medium text-text-muted uppercase mb-1">Type</label>
                <select
                  value={referralTypeFilter}
                  onChange={(e) =>
                    setReferralTypeFilter(e.target.value as 'ALL' | 'DOCTOR' | 'EMPLOYEE' | 'OTHER')
                  }
                  className="w-full px-2 py-2 rounded-lg border border-border bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                >
                  <option value="ALL">All</option>
                  <option value="DOCTOR">Doctor</option>
                  <option value="EMPLOYEE">Employee</option>
                  <option value="OTHER">Other</option>
                </select>
              </div>
              <div>
                <div className="flex items-center justify-between gap-1 mb-1">
                  <label className="text-xs font-medium text-text-muted uppercase">Referral</label>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {referralId && (
                      <span className="text-[0.625rem] text-text-muted uppercase">
                        {referralType === 'DOCTOR' ? 'Doctor' : referralType === 'EMPLOYEE' ? 'Employee' : 'Other'}
                      </span>
                    )}
                    {referralId && (
                      <button
                        type="button"
                        onClick={handleClearReferral}
                        className="text-[0.625rem] font-medium text-danger hover:underline uppercase shrink-0"
                      >
                        Clear
                      </button>
                    )}
                  </div>
                </div>
                <ReferralSearchSelect
                  value={referralId}
                  onChange={handleSelectReferral}
                  selectedLabel={referralName}
                  type={referralTypeFilter}
                  placeholder="Search referral..."
                  onAddNew={(query) => {
                    setQuickReferralQuery(query);
                    setShowQuickReferralModal(true);
                  }}
                  addNewLabel="Add referral"
                />
              </div>
              <div>
                <label className="flex items-center gap-1 text-xs font-medium text-text-muted mb-1 uppercase">
                  Referral %
                  <InfoHint
                    label="Referral %"
                    en="Share of the TEST bill paid to the referral. It defaults to the hospital's allocated base discount minus the patient's item discount (e.g. base 40% − patient 20% = referral 20%). Paid by the hospital, never added to the patient's bill. Overrides the auto share for this receipt only."
                    bn="রেফারেল টেস্ট বিলের যে অংশ পাবে। ডিফল্টভাবে হাসপাতালের বরাদ্দ বেস ডিসকাউন্ট থেকে রোগীর আইটেম ডিসকাউন্ট বাদ দিয়ে হিসাব হয় (যেমন বেস ৪০% − রোগী ২০% = রেফারেল ২০%)। হাসপাতাল এটি বহন করে; রোগীর বিলে যোগ হয় না। শুধুমাত্র এই রিসিটের জন্য অটো শতাংশ পরিবর্তন করে।"
                  />
                </label>
                <div className="flex items-center gap-1">
                  <ValidatedNumberInput
                    value={referralSharePercent}
                    onChange={(value) => {
                      setReferralShareTouched(true);
                      setReferralSharePercent(value);
                    }}
                    disabled={!referralId}
                    max={100}
                    placeholder="0.00"
                    className={`w-full px-3 py-2 rounded-lg border bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary text-sm disabled:opacity-60 ${
                      referralPayoutIsZero ? 'border-warning ring-1 ring-warning/40' : 'border-border'
                    }`}
                  />
                  <span className="text-sm text-text-muted">%</span>
                </div>
                {referralPayoutIsZero && (
                  <p className="text-[0.625rem] text-warning mt-1 leading-snug">
                    No payout will be created in Billing → Payouts.
                  </p>
                )}
              </div>
            </div>

            {referralId && effectiveReferralPercent > 0 && totalDiscountAmount > 0 && (
              <p className="text-[0.6875rem] text-text-muted leading-relaxed">
                <span className="font-medium text-text">Referral:</span>{' '}
                {Number(effectiveReferralPercent.toFixed(2))}% of the test bill
                {' '}(base {effectiveBasePercent}% − patient {Number(itemDiscountPercent.toFixed(2))}%) ={' '}
                ৳{referralCommissionAmount.toLocaleString()} · paid by the hospital
              </p>
            )}

            {/* Note */}
            <div>
              <label className="block text-xs font-medium text-text-muted mb-1 uppercase">Note</label>
              <textarea
                value={doctorRecommendation}
                onChange={(e) => setDoctorRecommendation(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-border bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary text-sm min-h-[80px] resize-y"
                placeholder="Prescribed tests, urgency..."
                rows={3}
              />
            </div>

            <div className="h-px bg-border" />

            {/* Payment */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-text-muted mb-1 uppercase">Payment Mode</label>
                <select
                  value={paymentMode}
                  onChange={(e) => setPaymentMode(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                >
                  <option value="cash">Cash</option>
                  <option value="card">Card</option>
                  <option value="upi">UPI</option>
                  <option value="bank_transfer">Bank Transfer</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-text-muted mb-1 uppercase">Transaction Ref</label>
                <input
                  type="text"
                  value={transactionReference}
                  onChange={(e) => setTransactionReference(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                  placeholder="Optional"
                />
              </div>
            </div>

            <div className="h-px bg-border" />

            {/* Discount & Received */}
            {canOverrideBaseDiscount && (
              <div>
                <label className="flex items-center gap-1 text-xs font-medium text-text-muted mb-1 uppercase">
                  Receipt Base Discount (%)
                  <InfoHint
                    label="Receipt Base Discount (%)"
                    en="Maximum discount allowed on this receipt. It overrides the hospital-wide Receipt Base Discount in Settings → General for this receipt only and defaults to 100%."
                    bn="এই রিসিটে সর্বোচ্চ অনুমোদিত ডিসকাউন্ট। এটি শুধু এই রিসিটের জন্য Settings → General-এর Receipt Base Discount-কে ওভাররাইড করে এবং ডিফল্ট ১০০%।"
                  />
                </label>
                <ValidatedNumberInput
                  value={baseDiscountOverride}
                  onChange={(value) => setBaseDiscountOverride(value)}
                  max={100}
                  placeholder="100"
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                />
                <p className="text-[0.625rem] text-text-muted mt-1 leading-snug">
                  Overrides the {referralBasePercent}% from Settings → General for this receipt.
                </p>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-text-muted mb-1 uppercase">Discount %</label>
                <input
                  type="number"
                  min={0}
                  max={100}
                  value={itemDiscountPercent}
                  readOnly
                  className={`w-full px-3 py-2 rounded-lg border bg-background/50 text-text focus:outline-none text-sm cursor-not-allowed ${
                    discountExceedsBase ? 'border-danger ring-1 ring-danger/40' : 'border-border'
                  }`}
                />
                {discountExceedsBase && (
                  <p className="text-[0.6875rem] text-danger mt-1">
                    Max {effectiveBasePercent}% (Receipt Base Discount). Reduce the discount to save.
                  </p>
                )}
                {referralCommissionAmount > 0 && (
                  <p className="text-[0.625rem] text-text-muted mt-1 leading-snug">
                    The patient keeps this full discount; the {effectiveReferralPercent}% referral is paid by the hospital.
                  </p>
                )}
              </div>
              <div>
                <label className="block text-xs font-medium text-text-muted mb-1 uppercase">Extra Discount</label>
                <div className="flex items-stretch gap-1.5">
                  <ValidatedNumberInput
                    value={extraDiscountValue}
                    onChange={(value) => applyExtraDiscount(extraDiscountType, value)}
                    max={extraDiscountType === 'PERCENT' ? 100 : undefined}
                    placeholder="0"
                    className="w-full min-w-0 px-3 py-2 rounded-lg border border-border bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                  />
                  <div className="inline-flex shrink-0 rounded-lg border border-border overflow-hidden">
                    <button
                      type="button"
                      onClick={() => applyExtraDiscount('FLAT', extraDiscountValue)}
                      title="Flat amount"
                      className={`px-2.5 text-xs font-bold transition-colors ${
                        extraDiscountType === 'FLAT'
                          ? 'bg-primary text-white'
                          : 'bg-background text-text-muted hover:text-text'
                      }`}
                    >
                      ৳
                    </button>
                    <button
                      type="button"
                      onClick={() => applyExtraDiscount('PERCENT', extraDiscountValue)}
                      title="Percentage"
                      className={`px-2.5 text-xs font-bold border-l border-border transition-colors ${
                        extraDiscountType === 'PERCENT'
                          ? 'bg-primary text-white'
                          : 'bg-background text-text-muted hover:text-text'
                      }`}
                    >
                      %
                    </button>
                  </div>
                </div>
                {extraDiscountType === 'PERCENT' && extraDiscountAmount > 0 && (
                  <p className="text-[0.625rem] text-text-muted mt-1">
                    = ৳{extraDiscountAmount.toLocaleString()} off
                  </p>
                )}
              </div>
              <div className="col-span-2">
                <div className="flex items-center justify-between mb-1">
                  <label className="flex items-center gap-1 text-xs font-medium text-text-muted uppercase">
                    Received
                    <InfoHint
                      label="Received"
                      en="Amount actually collected from the patient. Once an amount is entered, it follows the Extra Discount so the receipt stays settled; a blank or 0 Received (unpaid receipt) is left at 0."
                      bn="রোগীর কাছ থেকে প্রকৃত আদায়কৃত টাকা। একবার টাকা লেখা হলে Extra Discount পরিবর্তনের সাথে এটি নতুন প্রদেয় অনুযায়ী আপডেট হয়; খালি বা ০ রাখলে (বাকি রিসিট) ০-ই থাকে।"
                    />
                  </label>
                  <button
                    type="button"
                    onClick={() => setPaidAmount(netPayableAmount)}
                    disabled={netPayableAmount <= 0}
                    className="text-[0.625rem] font-medium text-primary hover:underline uppercase disabled:opacity-50 disabled:no-underline"
                  >
                    Autofill ৳{netPayableAmount.toLocaleString()}
                  </button>
                </div>
                <ValidatedNumberInput
                  value={paidAmount}
                  onChange={(value) => setPaidAmount(Math.max(0, value))}
                  placeholder="0"
                  className={`w-full px-3 py-2 rounded-lg border bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary text-sm ${
                    isExcessReceived ? 'border-danger ring-1 ring-danger/40' : 'border-border'
                  }`}
                />
                {isExcessReceived ? (
                  <p className="text-[0.6875rem] text-danger mt-1">
                    Received exceeds the payable ৳{netPayableAmount.toLocaleString()} by ৳
                    {excessAmount.toLocaleString()}. Reduce it before saving.
                  </p>
                ) : (
                  <p className="text-[0.6875rem] text-text-muted mt-1">
                    Max ৳{netPayableAmount.toLocaleString()} (after discount)
                  </p>
                )}
              </div>
            </div>

            {/* Total */}
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-text">Total Amount</span>
              <span className="text-2xl font-bold text-primary">৳{totalAmount.toLocaleString()}</span>
            </div>
            <div className="space-y-1">
              {referralCommissionAmount > 0 && totalDiscountAmount > 0 && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-text-muted">
                    Item discount (
                    <span className="text-primary font-semibold">{itemDiscountPercent}%</span>)
                  </span>
                  <span className="text-text font-medium">৳{totalDiscountAmount.toLocaleString()}</span>
                </div>
              )}
              {referralPreview && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-text-muted">
                    Referral · hospital pays (
                    <span className="text-primary font-semibold">
                      {Number(referralPreview.percent.toFixed(2))}%
                    </span>{' '}
                    of the test bill
                    )
                  </span>
                  <span className="text-primary font-medium">৳{Math.round(referralPreview.doctorShare).toLocaleString()}</span>
                </div>
              )}
              <div className="space-y-1">
                {totalDiscountAmount > 0 && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-text-muted">
                      Discount (<span className="text-primary font-semibold">{itemDiscountPercent}%</span>)
                    </span>
                    <span className="text-danger font-medium">-৳{Number(totalDiscountAmount.toFixed(2)).toLocaleString()}</span>
                  </div>
                )}
                {extraDiscountAmount > 0 && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-text-muted">
                      Extra Discount
                      {extraDiscountType === 'PERCENT' && (
                        <>
                          {' ('}
                          <span className="text-primary font-semibold">{extraDiscountValue}%</span>
                          {')'}
                        </>
                      )}
                    </span>
                    <span className="text-danger font-medium">-৳{extraDiscountAmount.toLocaleString()}</span>
                  </div>
                )}
                {combinedDiscountAmount > 0 && (totalDiscountAmount > 0 || extraDiscountAmount > 0) && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-text-muted">
                      Total Discount (
                      <span className="text-primary font-semibold">{combinedDiscountPercent}%</span>)
                    </span>
                    <span className="text-danger font-bold">-৳{combinedDiscountAmount.toLocaleString()}</span>
                  </div>
                )}
              </div>
              {paidAmount > 0 && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-text-muted">Received</span>
                  <span className="text-success font-medium">৳{paidAmount.toLocaleString()}</span>
                </div>
              )}
            </div>

            {totalAmount > 0 && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-text-muted">
                  {isFullyPaid ? 'Status' : isExcessReceived ? 'Excess Received' : 'Due'}
                </span>
                {isFullyPaid ? (
                  <span className="text-success font-bold inline-flex items-center gap-1">
                    <CheckCircle2 size={14} />
                    Paid
                  </span>
                ) : isExcessReceived ? (
                  <span className="text-danger font-bold">৳{excessAmount.toLocaleString()}</span>
                ) : (
                  <span className="text-text font-bold">৳{dueAmount.toLocaleString()}</span>
                )}
              </div>
            )}

            <button
              onClick={handleSubmit}
              disabled={!patientId || selectedTests.size === 0 || createMutation.isPending || visitsLoading}
              className="w-full px-4 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {createMutation.isPending ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <Check size={18} />
              )}
              {createMutation.isPending ? 'Creating...' : 'Create Receipt'}
            </button>
          </div>
        </div>
      </div>

      {/* The selected patient already owes on another receipt — offer to add
          the new test there instead of opening a second receipt. */}
      <DueReceiptPromptModal
        open={showDueReceiptPrompt}
        mode="create"
        patientName={selectedPatient ? joinFullName(selectedPatient) : undefined}
        patientMobile={selectedPatient?.mobileNumber}
        receipts={patientDueReceipts}
        onOpenReceipt={(receiptId) =>
          navigate(`/opd/diagnostic-receipts/${receiptId}`, {
            state: { openedFromDuePrompt: true },
          })
        }
        onSkip={() => setDismissedDuePromptKey(duePromptKey)}
      />

      <QuickPatientModal
        open={showQuickPatientModal}
        onClose={() => setShowQuickPatientModal(false)}
        initialQuery={patientQuery}
        onCreated={(patient) => {
          setPatientId(patient.id);
          setFormError(null);
        }}
        onOpenFullForm={() => navigate('/patients/new')}
      />

      {/* Quick Referral Modal (referral partner) */}
      <QuickReferralModal
        open={showQuickReferralModal}
        initialQuery={quickReferralQuery}
        onClose={() => setShowQuickReferralModal(false)}
        onCreated={handleReferralCreated}
      />

      {/* Quick Add Doctor Modal (doctor reference) */}
      <QuickDoctorModal
        open={showQuickDoctorModal}
        initialQuery={quickDoctorQuery}
        onClose={() => setShowQuickDoctorModal(false)}
        onCreated={handleQuickDoctorCreated}
      />

      {/* Received-too-high warning — blocks create until the amount is valid */}
      <ReceivedExcessModal
        open={showPayableWarning}
        details={{ totalAmount, discountAmount: patientDiscountAmount, netPayable: netPayableAmount, received: paidAmount, excess: excessAmount }}
        onClose={() => setShowPayableWarning(false)}
        onSetToPayable={() => {
          setPaidAmount(netPayableAmount);
          setShowPayableWarning(false);
        }}
      />

      {/* Discount above the Receipt Base Discount — blocks create until fixed */}
      <ReceiptDiscountLimitModal
        open={showDiscountLimitWarning}
        details={{
          discountPercent: combinedDiscountPercent,
          basePercent: effectiveBasePercent,
        }}
        onClose={() => setShowDiscountLimitWarning(false)}
      />

      {/* Referral selected but no payout would be booked — requires confirmation */}
      <ReferralZeroPayoutModal
        open={showReferralPayoutWarning}
        details={{
          referralName,
          referralPercent: effectiveReferralPercent,
          projectedPayout: referralProjection?.doctorShare ?? 0,
          reason: referralZeroReason,
        }}
        onClose={() => setShowReferralPayoutWarning(false)}
        onProceed={handleCreateWithoutPayout}
      />

      {/* Success Modal */}
      {showSuccessModal && createdReceipt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-surface rounded-xl border border-border p-6 w-full max-w-sm space-y-4 shadow-xl mx-4">
            <div className="flex flex-col items-center text-center space-y-2">
              <div className="w-12 h-12 rounded-full bg-success/10 flex items-center justify-center">
                <CheckCircle2 size={24} className="text-success" />
              </div>
              <h3 className="text-lg font-bold text-text">Receipt Created!</h3>
              <p className="text-sm text-text-muted">
                Receipt <span className="font-mono font-medium text-text">{createdReceipt.receiptNumber}</span> has been created successfully.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3 pt-2">
              <button
                onClick={handlePrintAndGo}
                className="px-4 py-2.5 bg-surface border border-border text-text rounded-lg hover:bg-background transition-colors text-sm font-medium flex items-center justify-center gap-2"
              >
                <Printer size={16} />
                Print
              </button>
              <button
                onClick={handleGo}
                className="px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium flex items-center justify-center gap-2"
              >
                Go
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
